import React, { useState } from 'react';
import { Upload, Eye, Download, Trash2, Save, Image as ImageIcon, FileText, X, Check, ChevronDown, Info } from 'lucide-react';
import { useLogo } from './LogoContext';
import SafeHtml from './components/SafeHtml';

const SchoolLogoSettings = ({ onClose }) => {
  const {
    logo,
    logoName,
    logoSize,
    saveStatus,
    saveLogo,
    removeLogo
  } = useLogo();

  const [dragActive, setDragActive] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [tempLogo, setTempLogo] = useState(null);
  const [tempLogoName, setTempLogoName] = useState('');
  const [tempLogoSize, setTempLogoSize] = useState(logoSize || 120);
  const [tempLogoFileSize, setTempLogoFileSize] = useState(0);

  const handleFileUpload = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, JPG, SVG, etc.)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Logo = e.target.result;
      setTempLogo(base64Logo);
      setTempLogoName(file.name);
      setTempLogoFileSize(file.size);
      setTempLogoSize(logoSize || 120);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  };

  const handleSave = () => {
    const logoToSave = tempLogo || logo;

    if (!logoToSave) {
      alert('Please select a logo first');
      return;
    }

    const success = saveLogo(logoToSave, tempLogoName || logoName, tempLogoSize || logoSize || 120);
    if (success) {
      setTempLogo(null);
      setTempLogoName('');
      setTempLogoSize(logoSize || 120);
      setTempLogoFileSize(0);
    } else {
      alert('Error saving logo. Please try again.');
    }
  };

  const handleRemove = () => {
    if (window.confirm('Are you sure you want to remove the school logo?')) {
      removeLogo();
      setTempLogo(null);
      setTempLogoName('');
      setTempLogoSize(logoSize || 120);
      setTempLogoFileSize(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const displayLogo = tempLogo || logo;
  const displayName = tempLogoName || logoName || '';
  const displayFileSize = tempLogoFileSize || 0;
  const displayHeight = tempLogoSize || logoSize || 120;
  const hasUnsavedChanges = Boolean(tempLogo);

  const getExportPreview = () => {
    const currentDate = new Date().toLocaleDateString('en-CA');
    const currentTime = new Date().toLocaleTimeString('en-CA');
    const logoToUse = tempLogo || logo;

    return `<!DOCTYPE html>
<html>
<head>
  <title>Lecture Template Preview</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; background: #f8fafc; }
    .header { text-align: center; margin-bottom: 30px; background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .logo { max-height: ${displayHeight}px; margin-bottom: 10px; }
    .course-title { color: #1e40af; margin: 10px 0; font-size: 28px; font-weight: bold; }
    .meta { color: #666; font-size: 14px; margin: 5px 0; }
    .content { background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); margin-top: 20px; }
    .section-title { color: #1e40af; font-size: 20px; font-weight: bold; margin-bottom: 15px; }
  </style>
</head>
<body>
  <div class="header">
    ${logoToUse ? `<img src="${logoToUse}" alt="School Logo" class="logo" />` : ''}
    <h1 class="course-title">📚 Course Topic - Week 1</h1>
    <div class="meta">
      Instructor: Your Name | email@institution.edu<br>
      Generated: ${currentDate} | ${currentTime}
    </div>
  </div>

  <div class="content">
    <h2 class="section-title">Session Overview</h2>
    <p>Welcome to this week's lesson. This session covers the main learning objectives and key concepts that students will explore and master.</p>
  </div>
</body>
</html>`;
  };

  const downloadSample = () => {
    const htmlContent = getExportPreview();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample_lecture_with_logo.html';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-800 to-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <ImageIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white leading-tight">School Logo</h2>
              <p className="text-xs text-slate-300">Appears on exports, PDFs, and every slide's bottom-left corner</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-300 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-5">
          {/* Save status banner */}
          {saveStatus && (
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium ${
              saveStatus === 'saved'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-amber-50 text-amber-800 border border-amber-200'
            }`}>
              <Check className="h-4 w-4 flex-shrink-0" />
              {saveStatus === 'saved'
                ? 'Logo saved — it now appears in all exports and slides.'
                : 'Logo removed.'}
            </div>
          )}

          {/* Preview / dropzone */}
          {displayLogo ? (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-center bg-[linear-gradient(45deg,#f8fafc_25%,transparent_25%,transparent_75%,#f8fafc_75%),linear-gradient(45deg,#f8fafc_25%,#fff_25%,#fff_75%,#f8fafc_75%)] bg-[length:16px_16px] bg-[position:0_0,8px_8px] p-8">
                <img
                  src={displayLogo}
                  alt="School logo preview"
                  className="max-w-full object-contain"
                  style={{ maxHeight: `${displayHeight}px` }}
                />
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-t border-gray-100">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{displayName || 'Current logo'}</p>
                  <p className="text-xs text-gray-500">
                    {displayFileSize ? formatFileSize(displayFileSize) : 'Saved logo'}
                    {hasUnsavedChanges && <span className="ml-2 text-amber-600 font-medium">• Unsaved</span>}
                  </p>
                </div>
                <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white cursor-pointer transition-colors">
                  <Upload className="h-4 w-4" />
                  Replace
                  <input type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
                </label>
              </div>
            </div>
          ) : (
            <div
              className={`rounded-xl border-2 border-dashed transition-colors p-8 text-center ${
                dragActive ? 'border-slate-500 bg-slate-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
              }`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
            >
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                <Upload className="h-6 w-6 text-slate-500" />
              </div>
              <p className="text-gray-700 font-medium">
                Drag &amp; drop your logo, or{' '}
                <label className="text-slate-700 underline hover:text-slate-900 cursor-pointer">
                  browse files
                  <input type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
                </label>
              </p>
              <p className="text-xs text-gray-500 mt-1">PNG, JPG, SVG or GIF · up to 5&nbsp;MB · transparent PNG works best</p>
            </div>
          )}

          {/* Size control */}
          {displayLogo && (
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Display size</label>
                <span className="text-sm font-semibold text-slate-700 tabular-nums">{displayHeight}px</span>
              </div>
              <input
                type="range"
                min="40"
                max="240"
                value={displayHeight}
                onChange={(e) => setTempLogoSize(parseInt(e.target.value, 10) || 120)}
                className="w-full accent-slate-700"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Controls how large the logo appears in HTML/PDF exports and the bottom-left corner of each slide (including PowerPoint).
              </p>
            </div>
          )}

          {/* Primary actions */}
          {displayLogo && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={!hasUnsavedChanges && saveStatus !== ''}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60"
              >
                <Save className="h-4 w-4" />
                {hasUnsavedChanges ? 'Save logo' : 'Saved'}
              </button>
              <button
                onClick={handleRemove}
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-sm font-semibold transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Remove
              </button>
            </div>
          )}

          {/* Secondary tools */}
          {displayLogo && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Eye className="h-4 w-4" /> Preview in export
              </button>
              <button
                onClick={downloadSample}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Download className="h-4 w-4" /> Download sample
              </button>
            </div>
          )}

          {/* Guidelines (collapsible) */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowGuidelines((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <span className="flex items-center gap-2"><Info className="h-4 w-4 text-slate-500" /> Logo tips &amp; where it appears</span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showGuidelines ? 'rotate-180' : ''}`} />
            </button>
            {showGuidelines && (
              <div className="px-4 pb-4 pt-1 text-sm text-gray-600 space-y-1.5 border-t border-gray-100">
                <p>• Use a high-resolution image, ideally 200–400px wide.</p>
                <p>• A transparent-background PNG looks best over colored slides.</p>
                <p>• The logo is resized automatically to fit each export.</p>
                <p>• It appears in HTML &amp; PDF exports, print documents, and the bottom-left of every slide (on screen and in PowerPoint).</p>
                <p>• Stored for this session only — it clears when you restart.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Export preview modal */}
      {showPreview && displayLogo && (
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-2xl max-w-3xl max-h-[85vh] overflow-hidden flex flex-col w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" /> Export preview
              </h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto">
              <SafeHtml
                className="border border-gray-200 rounded-lg overflow-hidden"
                html={getExportPreview()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolLogoSettings;
